import { useEffect } from 'react';
import DOMPurify from 'dompurify';

const ConsignmentAgreementPage = () => {
  useEffect(() => {
    document.title = 'Consignment Agreement | Only Skullz';
  }, []);

  // Add row function - using sanitized HTML
  const addProductRow = () => {
    const tbody = document.getElementById('productTableBody');
    const newRow = document.createElement('tr');
    const rowHTML = `
      <td><input type="text" class="product-name" placeholder="Product name" /></td>
      <td><input type="number" class="retail-price" placeholder="$0.00" step="0.01" /></td>
      <td><input type="number" class="commission-pct" placeholder="%" step="1" min="0" max="100" /></td>
      <td><input type="text" class="consignor-amount" placeholder="$0.00" readonly /></td>
      <td class="commission-col"><input type="text" class="consignee-earnings" placeholder="$0.00" readonly /></td>
      <td><input type="number" class="qty" placeholder="0" min="1" /></td>
      <td class="action-col"><button type="button" class="remove-row-btn">X</button></td>
    `;
    newRow.innerHTML = DOMPurify.sanitize(rowHTML, {
      ADD_TAGS: ['input', 'button'],
      ADD_ATTR: ['type', 'class', 'placeholder', 'step', 'min', 'max', 'readonly']
    });
    tbody.appendChild(newRow);
    
    // Add event listeners for calculation
    const retailInput = newRow.querySelector('.retail-price');
    const commissionInput = newRow.querySelector('.commission-pct');
    const removeBtn = newRow.querySelector('.remove-row-btn');
    
    retailInput.addEventListener('input', () => calculateRow(newRow));
    commissionInput.addEventListener('input', () => calculateRow(newRow));
    removeBtn.addEventListener('click', () => newRow.remove());
  };

  const calculateRow = (row) => {
    const retailPrice = parseFloat(row.querySelector('.retail-price').value) || 0;
    const commission = parseFloat(row.querySelector('.commission-pct').value) || 0;
    const consignorAmount = row.querySelector('.consignor-amount');
    const consigneeEarnings = row.querySelector('.consignee-earnings');
    
    if (retailPrice && commission >= 0 && commission <= 100) {
      const consigneeAmount = retailPrice * (commission / 100);
      const consignorAmountValue = retailPrice - consigneeAmount;
      
      consignorAmount.value = '$' + consignorAmountValue.toFixed(2);
      consigneeEarnings.value = '$' + consigneeAmount.toFixed(2);
    } else {
      consignorAmount.value = '';
      consigneeEarnings.value = '';
    }
  };

  const handleInputChange = (e) => {
    const row = e.target.closest('tr');
    if (row) calculateRow(row);
  };

  const removeLastRow = () => {
    const tbody = document.getElementById('productTableBody');
    const rows = tbody.getElementsByTagName('tr');
    if (rows.length > 1) {
      tbody.removeChild(rows[rows.length - 1]);
    } else {
      alert('You must have at least one product row.');
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Inter:wght@300;400;600&display=swap');
        
        .agreement-page * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        @page {
          size: letter;
          margin: 0.35in;
        }
        
        @page :first {
          margin-top: 0.2in;
        }
        
        .agreement-page {
          font-family: 'Inter', sans-serif;
          background: #fff;
          color: #000;
          line-height: 1.25;
          font-size: 8.5pt;
        }
        
        .agreement-page .container {
          max-width: 7.5in;
          margin: 0 auto;
          background: #fff;
          padding: 0;
        }
        
        .agreement-page .party-box,
        .agreement-page .term-item,
        .agreement-page .checkbox-group,
        .agreement-page .signature-box,
        .agreement-page .product-table tr {
          page-break-inside: avoid;
        }
        
        .agreement-page .page-break {
          page-break-before: always;
        }
        
        .agreement-page .header {
          text-align: center;
          padding: 8px 0;
          border-bottom: 2px solid #8b0000;
          margin-bottom: 12px;
          page-break-after: avoid;
        }
        
        .agreement-page .header h1 {
          font-family: 'Cinzel', serif;
          color: #000;
          font-size: 16pt;
          margin-bottom: 2px;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        
        .agreement-page .header p {
          color: #444;
          font-size: 8pt;
          letter-spacing: 1px;
          text-transform: uppercase;
        }
        
        .agreement-page .consignment-badge {
          display: inline-block;
          color: #8b0000;
          padding: 1px 5px;
          font-size: 6pt;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-left: 4px;
          border: 1px solid #8b0000;
        }
        
        .agreement-page .section-title {
          font-family: 'Cinzel', serif;
          color: #000;
          font-size: 10pt;
          margin: 10px 0 6px 0;
          padding-bottom: 2px;
          border-bottom: 2px solid #8b0000;
          text-transform: uppercase;
          letter-spacing: 1px;
          page-break-after: avoid;
        }
        
        .agreement-page .party-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 15px;
        }
        
        .agreement-page .party-box {
          border: 1px solid #333;
          padding: 10px;
          background: #fafafa;
        }
        
        .agreement-page .party-box h3 {
          color: #000;
          margin-bottom: 8px;
          font-size: 8pt;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-family: 'Cinzel', serif;
          border-bottom: 1px solid #8b0000;
          padding-bottom: 2px;
        }
        
        .agreement-page .form-group {
          margin-bottom: 5px;
        }
        
        .agreement-page label {
          display: block;
          color: #333;
          font-size: 6.5pt;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 1px;
        }
        
        .agreement-page input[type="text"],
        .agreement-page input[type="email"],
        .agreement-page input[type="tel"],
        .agreement-page input[type="date"],
        .agreement-page input[type="number"],
        .agreement-page select {
          width: 100%;
          padding: 2px 4px;
          border: 1px solid #999;
          font-family: 'Inter', sans-serif;
          font-size: 7.5pt;
          background: #fff;
          color: #000;
          height: 18px;
        }
        
        .agreement-page input[readonly] {
          background: #f0f0f0;
          color: #555;
        }
        
        .agreement-page .terms-section {
          border: 1px solid #ccc;
          padding: 10px;
          margin-bottom: 10px;
          background: #fafafa;
        }
        
        .agreement-page .term-item {
          margin-bottom: 8px;
          padding-left: 20px;
          position: relative;
        }
        
        .agreement-page .term-number {
          position: absolute;
          left: 0;
          top: 0;
          background: #8b0000;
          color: #fff;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 6.5pt;
          font-weight: 700;
          font-family: 'Cinzel', serif;
        }
        
        .agreement-page .term-item h4 {
          color: #000;
          margin-bottom: 2px;
          font-size: 7.5pt;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: 'Cinzel', serif;
        }
        
        .agreement-page .term-item p {
          color: #333;
          line-height: 1.3;
          font-size: 7.5pt;
          text-align: justify;
        }
        
        .agreement-page .highlight-box {
          background: #f5f5f5;
          border: 1px solid #8b0000;
          color: #000;
          padding: 8px;
          margin: 10px 0;
          text-align: center;
          page-break-inside: avoid;
        }
        
        .agreement-page .highlight-box h4 {
          font-size: 8pt;
          margin-bottom: 3px;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-family: 'Cinzel', serif;
          color: #8b0000;
        }
        
        .agreement-page .highlight-box p {
          color: #333;
          line-height: 1.3;
          font-size: 7.5pt;
        }
        
        .agreement-page .warning-box {
          background: #fff5f5;
          border: 1px solid #8b0000;
          border-left: 3px solid #8b0000;
          padding: 8px;
          margin: 10px 0;
          page-break-inside: avoid;
        }
        
        .agreement-page .warning-box h4 {
          color: #8b0000;
          margin-bottom: 4px;
          font-family: 'Cinzel', serif;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-size: 7.5pt;
        }
        
        .agreement-page .warning-box p {
          color: #333;
          font-size: 7.5pt;
          line-height: 1.3;
        }
        
        .agreement-page .checkbox-group {
          display: flex;
          align-items: flex-start;
          gap: 6px;
          margin: 4px 0;
          padding: 4px;
          border: 1px solid #ccc;
          background: #fafafa;
          cursor: pointer;
        }
        
        .agreement-page input[type="checkbox"] {
          width: 11px;
          height: 11px;
          margin-top: 1px;
          flex-shrink: 0;
        }
        
        .agreement-page .checkbox-group span {
          color: #333;
          font-size: 7.5pt;
          line-height: 1.15;
        }
        
        .agreement-page .signature-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #333;
        }
        
        .agreement-page .signature-box {
          text-align: center;
        }
        
        .agreement-page .signature-box h4 {
          color: #000;
          margin-bottom: 6px;
          font-family: 'Cinzel', serif;
          text-transform: uppercase;
          letter-spacing: 1px;
          font-size: 8pt;
          border-bottom: 1px solid #8b0000;
          padding-bottom: 2px;
        }
        
        .agreement-page .signature-line {
          border-bottom: 1px solid #000;
          height: 25px;
          margin-bottom: 2px;
          position: relative;
          background: #fff;
        }
        
        .agreement-page .signature-label {
          font-weight: 600;
          color: #555;
          text-transform: uppercase;
          font-size: 6.5pt;
          letter-spacing: 1px;
        }
        
        .agreement-page .date-field {
          margin-top: 4px;
        }
        
        .agreement-page .product-table {
          width: 100%;
          border-collapse: collapse;
          margin: 8px 0;
          font-size: 8pt;
          page-break-inside: auto;
        }
        
        .agreement-page .product-table thead {
          display: table-header-group;
        }
        
        .agreement-page .product-table tr {
          page-break-inside: avoid;
        }
        
        .agreement-page .product-table th {
          background: #f0f0f0;
          color: #000;
          padding: 5px 3px;
          text-align: left;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 7pt;
          letter-spacing: 0.5px;
          border: 1px solid #999;
          border-bottom: 2px solid #8b0000;
        }
        
        .agreement-page .product-table td {
          padding: 4px 3px;
          border: 1px solid #ccc;
          color: #000;
          background: #fff;
        }
        
        .agreement-page .product-table input {
          border: 1px solid #999;
          padding: 2px;
          width: 100%;
          background: #fff;
          color: #000;
          font-size: 8pt;
          height: 20px;
        }
        
        .agreement-page .product-table th:nth-child(3),
        .agreement-page .product-table td:nth-child(3),
        .agreement-page .product-table th:nth-child(4),
        .agreement-page .product-table td:nth-child(4),
        .agreement-page .product-table th:nth-child(5),
        .agreement-page .product-table td:nth-child(5),
        .agreement-page .product-table th:nth-child(6),
        .agreement-page .product-table td:nth-child(6) {
          text-align: center;
        }
        
        .agreement-page .commission-col {
          background: #f9f9f9;
        }
        
        .agreement-page strong {
          color: #000;
          font-weight: 700;
        }
        
        .agreement-page .button-container {
          margin: 8px 0;
          text-align: center;
        }
        
        .agreement-page .add-row-btn,
        .agreement-page .remove-row-btn {
          background: #8b0000;
          color: #fff;
          border: none;
          padding: 5px 10px;
          margin: 0 4px;
          cursor: pointer;
          font-size: 8pt;
          font-family: 'Inter', sans-serif;
          border-radius: 3px;
        }
        
        .agreement-page .remove-row-btn {
          background: #666;
        }
        
        .agreement-page .add-row-btn:hover {
          background: #a00000;
        }
        
        .agreement-page .remove-row-btn:hover {
          background: #888;
        }
        
        .agreement-page .action-col {
          width: 45px;
          text-align: center;
        }
        
        .agreement-page .print-button {
          display: block;
          width: 160px;
          margin: 12px auto;
          padding: 10px;
          background: #8b0000;
          color: #fff;
          border: none;
          font-size: 10pt;
          cursor: pointer;
          text-align: center;
          font-family: 'Cinzel', serif;
          text-transform: uppercase;
          letter-spacing: 2px;
        }
        
        .agreement-page .print-button:hover {
          background: #a00000;
        }
        
        .agreement-page .compact-row {
          display: flex;
          gap: 10px;
        }
        
        .agreement-page .compact-row .form-group {
          flex: 1;
        }
        
        .agreement-page .payment-option {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;
          min-width: 180px;
        }
        
        .agreement-page .payment-option input[type="checkbox"] {
          margin-right: 4px;
        }
        
        .agreement-page .payment-option input[type="text"] {
          flex: 1;
          margin-left: 4px;
        }
        
        .agreement-page .payment-instructions-section {
          margin-bottom: 25px;
        }
        
        @media print {
          /* Hide agreement page buttons and controls */
          .agreement-page .print-button,
          .agreement-page .button-container,
          .agreement-page .add-row-btn,
          .agreement-page .remove-row-btn,
          .agreement-page .action-col,
          .agreement-page .action-header,
          .agreement-page .back-link {
            display: none !important;
          }
          
          /* Hide website navbar, footer, and other non-document elements globally */
          [data-testid="navbar"],
          nav,
          footer,
          body > div > nav,
          body > div > footer,
          body > div > header,
          .App > nav,
          .App > footer,
          [class*="Navbar"],
          [class*="Footer"],
          .purchase-notification,
          [class*="toaster"],
          [class*="sonner"],
          [class*="emergent"],
          .visitor-count,
          [class*="visitor"],
          .made-with {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          
          /* Ensure clean print */
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          .App, .min-h-screen, .bg-void {
            background: white !important;
            min-height: auto !important;
          }
          
          main {
            padding: 0 !important;
            margin: 0 !important;
          }
          
          .agreement-page {
            background: white !important;
            padding: 0 !important;
          }
          
          .agreement-page .container {
            box-shadow: none !important;
            max-width: 100% !important;
          }
        }
        
        @media screen {
          .agreement-page {
            background: #e0e0e0;
            padding: 15px;
            min-height: 100vh;
          }
          
          .agreement-page .container {
            box-shadow: 0 0 20px rgba(0,0,0,0.1);
            padding: 25px;
            background: white;
          }
        }
        
        .agreement-page .back-link {
          display: block;
          text-align: center;
          margin-bottom: 15px;
          color: #8b0000;
          text-decoration: none;
          font-family: 'Cinzel', serif;
          text-transform: uppercase;
          letter-spacing: 2px;
          font-size: 10pt;
        }
        
        .agreement-page .back-link:hover {
          text-decoration: underline;
        }
      `}</style>
      
      <div className="agreement-page">
        <a href="/retailpartner" className="back-link">← Back to Partnership Options</a>
        
        <button className="print-button" onClick={() => window.print()}>Print / Save as PDF</button>
        
        <div className="container">
          {/* PAGE 1 */}
          <div className="header">
            <h1>Only Skullz <span className="consignment-badge">Consignment</span></h1>
            <p>Consignment Agreement & Authorization Form</p>
          </div>
          
          <div className="party-grid">
            <div className="party-box">
              <h3>Consignor (Seller)</h3>
              <div className="form-group">
                <label>Business Name</label>
                <input type="text" defaultValue="Only Skullz LLC" readOnly />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input type="text" defaultValue="2490 Tracy Lane, Aurora, IL 60506" readOnly />
              </div>
              <div className="compact-row">
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" defaultValue="Angel@OnlySkullz.com" readOnly />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input type="tel" defaultValue="630-945-1224" readOnly />
                </div>
              </div>
              <div className="form-group">
                <label>Contact Name</label>
                <input type="text" defaultValue="Angel Santoyo" readOnly />
              </div>
            </div>
            
            <div className="party-box">
              <h3>Consignee (Retailer)</h3>
              <div className="form-group">
                <label>Business Name *</label>
                <input type="text" placeholder="Enter business name" required />
              </div>
              <div className="form-group">
                <label>Business Address *</label>
                <input type="text" placeholder="Enter full address" required />
              </div>
              <div className="compact-row">
                <div className="form-group">
                  <label>Contact Name *</label>
                  <input type="text" placeholder="Contact person" required />
                </div>
                <div className="form-group">
                  <label>Phone *</label>
                  <input type="tel" placeholder="(XXX) XXX-XXXX" required />
                </div>
              </div>
              <div className="compact-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input type="email" placeholder="business@email.com" required />
                </div>
                <div className="form-group">
                  <label>Tax ID # *</label>
                  <input type="text" placeholder="Tax ID" required />
                </div>
              </div>
            </div>
          </div>
          
          <div className="section-title">Consignment Terms & Conditions</div>
          
          <div className="terms-section">
            <div className="term-item">
              <div className="term-number">1</div>
              <h4>Consignment Arrangement</h4>
              <p>Consignor (Only Skullz LLC) retains ownership of all products until sold to end consumers. Consignee agrees to display and sell products in their physical retail location only. Title and risk of loss remain with Consignor until final sale to consumer.</p>
            </div>
            
            <div className="term-item">
              <div className="term-number">2</div>
              <h4>Pricing & Revenue Split</h4>
              <p>Retail pricing shall be determined by Consignor and displayed on www.onlyskullz.com. Upon sale, Consignee retains agreed commission percentage (to be specified in Product List), and remits remaining balance to Consignor. Consignee may not discount items without written approval.</p>
            </div>
            
            <div className="highlight-box">
              <h4>Monthly Settlement Schedule</h4>
              <p>Consignee shall remit payment for all items sold during the previous month on the <strong>FIRST (1st) DAY OF EVERY MONTH</strong>. Payment must include detailed sales report and notification of sold items.</p>
            </div>
            
            <div className="term-item">
              <div className="term-number">3</div>
              <h4>Monthly Reporting & Notification</h4>
              <p>By the 1st of each month, Consignee must provide: Complete list of items sold with dates and prices; Current inventory count of unsold items; Payment for all items sold in previous month; Photo documentation of current display (if requested).</p>
            </div>
            
            <div className="term-item">
              <div className="term-number">4</div>
              <h4>Consignment Period & Returns</h4>
              <p>Initial consignment period is 90 days. Unsold items may be returned to Consignor at Consignee's expense or kept for additional 30-day periods by mutual agreement. Damaged items must be reported within 3 days with photo documentation.</p>
            </div>
            
            <div className="warning-box">
              <h4>Lost or Stolen Items Policy</h4>
              <p>Consignee assumes full financial responsibility for any lost, stolen, or unaccounted items that are not properly reported within the <strong>3-day notification period</strong> (or within 3 days of the monthly inventory report). For such items, Consignee must remit payment to Consignor for the agreed amount due after commission, calculated as if the items were sold. This obligation remains in effect even after termination of this agreement.</p>
            </div>
            
            <div className="term-item">
              <div className="term-number">5</div>
              <h4>Authorized Sales Channels</h4>
              <p>Products may ONLY be sold at the approved physical retail location listed above. Online sales, third-party marketplaces (Amazon, eBay, Walmart), or unauthorized locations are strictly prohibited without written consent.</p>
            </div>
            
            <div className="term-item">
              <div className="term-number">6</div>
              <h4>Brand Representation</h4>
              <p>Consignee agrees to maintain professional display of Only Skullz products, not alter packaging/branding, and promote brand positively. QR codes and promotional materials must be displayed prominently near products.</p>
            </div>
            
            <div className="term-item">
              <div className="term-number">7</div>
              <h4>Termination</h4>
              <p>Either party may terminate with 30 days written notice. Upon termination, Consignee must return all unsold inventory within 10 days or remit payment for items unable to be returned. Final settlement due within 15 days of termination.</p>
            </div>
          </div>
          
          {/* PAGE 2 */}
          <div className="page-break"></div>
          
          <div className="header">
            <h1>Only Skullz <span className="consignment-badge">Consignment</span></h1>
            <p>Consignment Agreement & Authorization Form - Page 2</p>
          </div>
          
          <div className="payment-instructions-section">
            <div className="section-title">Monthly Payment Instructions</div>
            
            <div className="form-group" style={{ marginBottom: '8px' }}>
              <label style={{ marginBottom: '6px' }}>Preferred Payment Method (check one):</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                <div className="payment-option">
                  <input type="checkbox" /> Check to Only Skullz LLC
                </div>
                <div className="payment-option">
                  <input type="checkbox" /> PayPal: Angel@OnlySkullz.com
                </div>
                <div className="payment-option">
                  <input type="checkbox" /> ACH Transfer
                </div>
                <div className="payment-option">
                  <input type="checkbox" /> Venmo
                </div>
                <div className="payment-option" style={{ minWidth: '250px' }}>
                  <input type="checkbox" /> Other:
                  <input type="text" placeholder="Specify payment method" style={{ flex: 1 }} />
                </div>
              </div>
            </div>
            
            <div className="highlight-box" style={{ margin: '12px 0', padding: '6px' }}>
              <h4 style={{ fontSize: '8pt' }}>Due Date: 1st of Every Month - Late payments may suspend privileges</h4>
            </div>
          </div>
          
          <div className="section-title">Acknowledgments</div>
          
          <p style={{ fontSize: '6.5pt', marginBottom: '4px', color: '#555' }}>By checking each box, Consignee acknowledges understanding and agreement:</p>
          
          <label className="checkbox-group">
            <input type="checkbox" />
            <span>I understand that Only Skullz LLC retains ownership of all consigned items until sold to end consumers, and I am responsible for safekeeping of inventory.</span>
          </label>
          
          <label className="checkbox-group">
            <input type="checkbox" />
            <span>I agree to remit payment and provide sales notifications on the 1st of every month for all items sold in the previous month.</span>
          </label>
          
          <label className="checkbox-group">
            <input type="checkbox" />
            <span>I understand that I am financially responsible for any lost, stolen, or unaccounted items not reported within the 3-day notification period, and must pay the agreed amount due after commission for such items as if they were sold.</span>
          </label>
          
          <label className="checkbox-group">
            <input type="checkbox" />
            <span>I confirm that all products will be sold only at the physical retail location specified above, not on third-party marketplaces or online without written approval.</span>
          </label>
          
          <label className="checkbox-group">
            <input type="checkbox" />
            <span>I authorize Only Skullz LLC to list my business name and location on www.onlyskullz.com as an authorized retail location.</span>
          </label>
          
          <label className="checkbox-group">
            <input type="checkbox" />
            <span>I have read, understand, and agree to all terms and conditions outlined in this Consignment Agreement.</span>
          </label>
          
          <div className="signature-grid">
            <div className="signature-box">
              <h4>Consignor (Only Skullz LLC)</h4>
              <div className="compact-row">
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" defaultValue="Angel Santoyo" readOnly style={{ textAlign: 'center' }} />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" defaultValue="Owner" readOnly style={{ textAlign: 'center' }} />
                </div>
              </div>
              <div className="signature-line"></div>
              <div className="signature-label">Signature</div>
              <div className="form-group date-field">
                <label>Date</label>
                <input type="text" placeholder="MM/DD/YYYY" style={{ textAlign: 'center' }} />
              </div>
            </div>
            
            <div className="signature-box">
              <h4>Consignee (Retailer)</h4>
              <div className="compact-row">
                <div className="form-group">
                  <label>Business Name *</label>
                  <input type="text" placeholder="Business name" style={{ textAlign: 'center' }} />
                </div>
                <div className="form-group">
                  <label>Authorized Signatory Name & Title *</label>
                  <input type="text" placeholder="Name, Title" style={{ textAlign: 'center' }} />
                </div>
              </div>
              <div className="signature-line"></div>
              <div className="signature-label">Signature</div>
              <div className="form-group date-field">
                <label>Date *</label>
                <input type="text" placeholder="MM/DD/YYYY" style={{ textAlign: 'center' }} />
              </div>
            </div>
          </div>
          
          {/* PAGE 3 */}
          <div className="page-break"></div>
          
          <div className="header">
            <h1>Only Skullz <span className="consignment-badge">Consignment</span></h1>
            <p>Consignment Product List - Page 3</p>
          </div>
          
          <div className="section-title">Consignment Product List</div>
          
          <div className="button-container">
            <button type="button" className="add-row-btn" onClick={addProductRow}>+ Add Row</button>
            <button type="button" className="remove-row-btn" onClick={removeLastRow}>- Remove Last Row</button>
          </div>
          
          <table className="product-table">
            <thead>
              <tr>
                <th style={{ width: '24%' }}>Item Description</th>
                <th style={{ width: '14%' }}>Retail Price</th>
                <th style={{ width: '14%' }}>Commission %</th>
                <th style={{ width: '17%' }}>Amount Due to Consignor</th>
                <th style={{ width: '17%' }} className="commission-col">Consignee Earnings</th>
                <th style={{ width: '8%' }}>Qty</th>
                <th className="action-header" style={{ width: '6%' }}>X</th>
              </tr>
            </thead>
            <tbody id="productTableBody">
              {[1, 2, 3, 4].map((_, idx) => (
                <tr key={idx}>
                  <td><input type="text" className="product-name" placeholder="Product name" /></td>
                  <td><input type="number" className="retail-price" placeholder="$0.00" step="0.01" onChange={handleInputChange} /></td>
                  <td><input type="number" className="commission-pct" placeholder="%" step="1" min="0" max="100" onChange={handleInputChange} /></td>
                  <td><input type="text" className="consignor-amount" placeholder="$0.00" readOnly /></td>
                  <td className="commission-col"><input type="text" className="consignee-earnings" placeholder="$0.00" readOnly /></td>
                  <td><input type="number" className="qty" placeholder="0" min="1" /></td>
                  <td className="action-col"><button type="button" className="remove-row-btn" onClick={(e) => {
                    const tbody = document.getElementById('productTableBody');
                    if (tbody.getElementsByTagName('tr').length > 1) {
                      e.target.closest('tr').remove();
                    } else {
                      alert('You must have at least one product row.');
                    }
                  }}>X</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          
          <div style={{ marginTop: '12px', paddingTop: '6px', borderTop: '1px solid #ccc', fontSize: '6.5pt', color: '#666', textAlign: 'center' }}>
            <p>Only Skullz LLC - Aurora, IL - Angel@OnlySkullz.com - www.OnlySkullz.com</p>
          </div>
        </div>
        
        <button className="print-button" onClick={() => window.print()}>Print / Save as PDF</button>
      </div>
    </>
  );
};

export default ConsignmentAgreementPage;
